import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clipboard, Activity, FileText, Receipt, DollarSign, Archive } from "lucide-react";
import { normalizeLegacyStatus, getJobStatusLabel, getJobStatusColor, type JobStatus } from "@/shared/types";

export type { JobStatus };

interface StatusBadgeProps {
  status: string;
  staffView?: boolean;
}

// For staff view: map invoiced/archived/paid to complete
function normalizeForStaff(status: string): string {
  const s = normalizeLegacyStatus(status);
  if (s === 'invoiced' || s === 'paid' || s === 'archived') return 'complete';
  return s;
}

export function StatusBadge({ status, staffView }: StatusBadgeProps) {
  const normalizedStatus = (staffView ? normalizeForStaff(status) : normalizeLegacyStatus(status)) as JobStatus;
  const color = getJobStatusColor(normalizedStatus);
  const label = getJobStatusLabel(normalizedStatus);
  
  const config: Record<JobStatus, {
    icon: typeof Clipboard;
  }> = {
    "quote-needed": {
      icon: Clipboard, // RED - Quote needed
    },
    "quoted": {
      icon: FileText, // ORANGE - Quote sent
    },
    "accepted": {
      icon: CheckCircle2, // GREEN - Quote accepted
    },
    "in-progress": {
      icon: Activity, // LIGHT BLUE - Job in progress
    },
    "complete": {
      icon: CheckCircle2, // DARK BLUE - Job completed
    },
    "invoiced": {
      icon: Receipt, // PURPLE - Invoice sent
    },
    "paid": {
      icon: DollarSign, // PINK - Payment received
    },
    "archived": {
      icon: Archive, // GREY - Job archived
    },
  };

  // Defensive check: use quote-needed as fallback if status not found
  const statusConfig = config[normalizedStatus] || config["quote-needed"];
  const Icon = statusConfig.icon;

  return (
    <Badge
      variant="default"
      style={{
        backgroundColor: color,
        color: 'white',
        fontWeight: 600,
        borderColor: color,
      }}
      className="gap-1 max-w-full overflow-hidden"
      data-testid={`badge-status-${normalizedStatus}`}
    >
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span className="truncate block">{label}</span>
    </Badge>
  );
}
